import { immer } from 'zustand/middleware/immer'

import { format } from '~/astra'
import { ScheduleSurgeryParams } from '~/hooks/useScheduleSurgerySaga/useScheduleSurgerySaga'
import { UnscheduleSurgeryParams } from '~/hooks/useUnscheduleSurgerySaga/useUnscheduleSurgerySaga'
import { ScheduledSurgery, Surgery } from '~/storm'
import { getBookingIdOrId } from '~/utils/dips'

import { Slice } from '../store'
import { DrawerActions, DrawerState, LocationDay } from './types'

type SelectedSurgery = {
    id: string
    isStrongMatch?: boolean
} | null

type SelectedSurgeryArgs = {
    surgery: Surgery | null
    isStrongMatch?: boolean
}

export type OpenDrawerArgs =
    | (LocationDay & {
          activeDrawerTab?: TabValue
      })
    | undefined

export type PreviewSurgery = { surgeryId: string; isStrongMatch: boolean; suggestionIndex?: number; filtersAppliedCount?: number }

export const tabs = [
    { label: 'Program', value: 'schedule' },
    { label: 'Kommentar', value: 'comments' },
    { label: 'Programhistorikk', value: 'historicData' },
] as const
export type TabValue = (typeof tabs)[number]['value']

type State = {
    selectedLocationDay: LocationDay | null
    needle: string
    selectedSurgery: SelectedSurgery
    schedulingSurgeries: {
        sagaId: string
        bookingId: string
        checkInTime: string
        plannedTheatreInTime: string
        resources: NonNullable<ScheduledSurgery['surgeryResources']>
    }[]
    unschedulingSurgeries: {
        sagaId: string
        bookingId: string
        postponementReason: number
    }[]
    previewSurgeries: PreviewSurgery[]
} & DrawerState<TabValue>

const initialState: State = {
    isDrawerOpen: false,
    activeDrawerTab: 'schedule',
    selectedLocationDay: null,
    needle: '',
    selectedSurgery: null,
    schedulingSurgeries: [],
    unschedulingSurgeries: [],
    previewSurgeries: [],
}

type Actions = {
    actions: {
        setSelectedSurgery: ({ surgery }: SelectedSurgeryArgs) => void
        setNeedle: (needle: string) => void
        addSchedulingSurgerySaga: (sagaId: string, params: ScheduleSurgeryParams) => void
        removeSchedulingSurgerySaga: (sagaId: string) => void
        addUnschedulingSurgerySaga: (sagaId: string, params: UnscheduleSurgeryParams) => void
        removeUnschedulingSurgerySaga: (sagaId: string) => void
        addPreviewSurgery: (previewSurgery: PreviewSurgery) => void
        removePreviewSurgery: (surgeryId: string) => void
        clearPreviewSurgeries: () => void
        unselectDay: () => void
    } & DrawerActions<TabValue, OpenDrawerArgs>
}

export type OperationalPlannerSlice = {
    operationalPlanner: State & Actions
}

export const createOperationalPlannerSlice: Slice<OperationalPlannerSlice> = immer(set => ({
    operationalPlanner: {
        ...initialState,
        actions: {
            openDrawer: (args?: OpenDrawerArgs) => {
                const { locationId, date, activeDrawerTab } = args || {}

                set(state => {
                    if (date !== undefined && locationId !== undefined) {
                        state.operationalPlanner.selectedLocationDay = {
                            date,
                            locationId,
                        }
                    }
                    state.operationalPlanner.isDrawerOpen = true
                    state.operationalPlanner.activeDrawerTab = activeDrawerTab ?? 'schedule' // Reset to 'schedule' by default
                })
            },
            unselectDay: () => {
                set(state => {
                    state.operationalPlanner.selectedLocationDay = null
                })
            },
            closeDrawer: () => {
                set(state => {
                    state.operationalPlanner.selectedLocationDay = null
                    state.operationalPlanner.isDrawerOpen = false
                })
            },
            toggleDrawer() {
                set(state => {
                    state.operationalPlanner.isDrawerOpen = !state.operationalPlanner.isDrawerOpen
                })
            },
            setActiveDrawerTab: (tabValue: TabValue) => {
                set(state => {
                    state.operationalPlanner.activeDrawerTab = tabValue
                })
            },
            setSelectedSurgery({ surgery }: SelectedSurgeryArgs) {
                set(state => {
                    state.operationalPlanner.selectedSurgery = surgery ? { id: getBookingIdOrId(surgery) } : null
                })
            },
            setNeedle(needle: string) {
                set(state => {
                    state.operationalPlanner.needle = needle
                })
            },
            addSchedulingSurgerySaga(sagaId: string, params: ScheduleSurgeryParams) {
                set(state => {
                    state.operationalPlanner.schedulingSurgeries = [
                        ...state.operationalPlanner.schedulingSurgeries,
                        {
                            sagaId,
                            bookingId: params.surgery.bookingId,
                            checkInTime: format(params.checkInTime),
                            plannedTheatreInTime: format(params.plannedTheatreInTime),
                            resources: params.resourceSlots ?? [],
                        },
                    ]
                })
            },
            removeSchedulingSurgerySaga(sagaId: string) {
                set(state => {
                    state.operationalPlanner.schedulingSurgeries = state.operationalPlanner.schedulingSurgeries.filter(
                        schedulingSurgery => schedulingSurgery.sagaId !== sagaId
                    )
                })
            },
            addUnschedulingSurgerySaga(sagaId: string, params: UnscheduleSurgeryParams) {
                set(state => {
                    state.operationalPlanner.unschedulingSurgeries = [
                        ...state.operationalPlanner.unschedulingSurgeries,
                        {
                            sagaId,
                            bookingId: params.surgery.id,
                            postponementReason: params.postponementReason,
                        },
                    ]
                })
            },
            removeUnschedulingSurgerySaga(sagaId: string) {
                set(state => {
                    state.operationalPlanner.unschedulingSurgeries = state.operationalPlanner.unschedulingSurgeries.filter(
                        unschedulingSurgery => unschedulingSurgery.sagaId !== sagaId
                    )
                })
            },
            addPreviewSurgery(previewSurgery: PreviewSurgery) {
                set(state => {
                    state.operationalPlanner.previewSurgeries = [...state.operationalPlanner.previewSurgeries, previewSurgery]
                })
            },
            removePreviewSurgery(surgeryId: string) {
                set(state => {
                    state.operationalPlanner.previewSurgeries = state.operationalPlanner.previewSurgeries.filter(
                        previewSurgery => previewSurgery.surgeryId !== surgeryId
                    )
                })
            },
            clearPreviewSurgeries() {
                set(state => {
                    state.operationalPlanner.previewSurgeries = []
                })
            },
        },
    },
}))
